<template>
  <q-card
    flat
    bordered
    class="checklist-item-card"
    :class="{ 'bg-green-1': item.is_completed }"
  >
    <q-card-section class="checklist-item-section">
      <!-- Header: checkbox + text + labels -->
      <div class="row no-wrap items-center">
        <q-checkbox
          :model-value="item.is_completed"
          :disable="toggling || isCheckboxDisabled"
          color="green-7"
          :size="$q.screen.lt.sm ? 'lg' : undefined"
          class="checkbox self-center"
          @update:model-value="$emit('toggle')"
        >
          <q-tooltip v-if="isCheckboxDisabled">
            {{ disabledReason }}
          </q-tooltip>
        </q-checkbox>

        <div class="col column justify-center">
          <span
            class="text-body2"
            :class="{ 'text-strike text-grey-5': item.is_completed }"
          >{{ item.text }}</span>

          <div
            v-if="hasLabels"
            class="row items-center q-gutter-x-sm q-mt-xs"
          >
            <span v-if="item.is_required" class="row inline items-center no-wrap label-dot">
              <q-icon
                name="mdi-asterisk"
                color="grey-7"
                size="15px"
              />
              <span class="text-caption text-grey-7">Required</span>
            </span>
            <span v-if="item.requires_photo" class="row inline items-center no-wrap label-dot">
              <q-icon
                name="mdi-camera"
                :color="dotColor(hasPhotos)"
                size="15px"
              />
              <span class="text-caption text-grey-7">{{ photoLabel }}</span>
            </span>
            <span v-if="item.requires_invoice" class="row inline items-center no-wrap label-dot">
              <q-icon
                name="mdi-file-document-outline"
                :color="dotColor(invoiceSatisfied)"
                size="15px"
              />
              <span class="text-caption text-grey-7">{{ invoiceLabel }}</span>
            </span>
            <span v-if="item.requires_quotation" class="row inline items-center no-wrap label-dot">
              <q-icon
                name="mdi-file-document-edit-outline"
                :color="dotColor(quotationSatisfied)"
                size="15px"
              />
              <span class="text-caption text-grey-7">{{ quotationLabel }}</span>
            </span>
            <span
              v-for="form in requiredForms"
              :key="form.id"
              class="row inline items-center no-wrap label-dot"
            >
              <q-icon
                name="mdi-clipboard-text-outline"
                :color="dotColor(form.satisfied)"
                size="15px"
              />
              <span class="text-caption text-grey-7">{{ form.label }}</span>
            </span>
          </div>

          <q-item-label
            v-if="fromRequiredForm"
            caption
            class="q-mt-xs text-blue-grey-7"
          >
            <q-icon name="mdi-link-variant" size="12px" />
            Added automatically — this template is required on the appointment
          </q-item-label>

          <q-item-label
            v-if="item.is_completed && item.completed_by_user"
            caption
            class="q-mt-xs"
          >
            {{ item.completed_by_user.name }}
            <template v-if="item.completed_at">
              &middot; {{ $formatSystemDate(item.completed_at, luxonFormats.FRIENDLY_DATE_TIME) }}
            </template>
          </q-item-label>
        </div>

        <!-- Assignee (right of the header) -->
        <q-avatar
          v-if="item.assigned_to_user"
          size="28px"
          class="self-center q-ml-sm"
          style="flex-shrink: 0;"
        >
          <profile-image
            no-animation
            size="28px"
            :initials="item.assigned_to_user.initials"
            :profile-image="item.assigned_to_user.profile_image"
            :color="item.assigned_to_user.color"
          />
          <q-tooltip>{{ item.assigned_to_user.name }}</q-tooltip>
        </q-avatar>

        <!-- Remove (manually added tasks only) -->
        <q-btn
          v-if="allowDelete"
          flat
          dense
          round
          size="sm"
          icon="mdi-delete"
          color="red-7"
          class="self-center q-ml-xs"
          style="flex-shrink: 0;"
          @click="$emit('delete')"
        >
          <q-tooltip>Remove task</q-tooltip>
        </q-btn>
      </div>

      <!-- Action buttons (separated from the item header above) -->
      <template v-if="hasActions || hasDocumentActions">
        <q-separator class="q-my-sm" />
        <div class="action-buttons">
          <!-- Editing actions: only while the task is still open -->
          <template v-if="hasActions">
            <q-btn
              v-if="!item.assigned_to_user"
              outline
              no-caps
              align="left"
              color="primary"
              icon="mdi-account-plus"
              label="Assign to staff"
              class="full-width"
              @click="showAssignPicker = true"
            />
            <q-btn
              v-else
              outline
              no-caps
              align="left"
              color="primary"
              icon="mdi-pencil"
              label="Edit assignee"
              class="full-width"
              @click="showAssignPicker = true"
            />

            <template v-if="item.requires_photo">
              <q-btn
                unelevated
                no-caps
                align="left"
                color="primary"
                icon="mdi-image-multiple"
                label="Browse files"
                class="full-width"
                @click="$emit('openFileDialog')"
              />
              <uploader-btn
                accept="image/*"
                capture="camera"
                class="full-width"
                :btn-props="{
                  unelevated: true,
                  noCaps: true,
                  align: 'left',
                  color: 'primary',
                  icon: 'mdi-camera',
                  label: $q.platform.is.mobile ? 'Take photo' : 'Upload photo',
                  class: 'full-width'
                }"
                @uploaded="(files) => $emit('uploaded', files)"
              />
            </template>
          </template>

          <!-- Document actions stay available after completion so the linked
               invoice / quote / report can still be viewed. -->
          <q-btn
            v-if="item.requires_invoice"
            unelevated
            no-caps
            align="left"
            :color="invoiceAction.color"
            :icon="invoiceAction.icon"
            :label="invoiceAction.label"
            class="full-width"
            @click="$emit('invoiceAction', invoiceAction)"
          />

          <q-btn
            v-if="item.requires_quotation"
            unelevated
            no-caps
            align="left"
            :color="quotationAction.color"
            :icon="quotationAction.icon"
            :label="quotationAction.label"
            class="full-width"
            @click="$emit('quotationAction', quotationAction)"
          />

          <q-btn
            v-for="form in requiredForms"
            :key="form.id"
            unelevated
            no-caps
            align="left"
            :color="form.action.color"
            :icon="form.action.icon"
            :label="form.action.label"
            class="full-width"
            @click="$emit('formAction', form.action)"
          />
        </div>
      </template>

      <!-- Attachments (own section, underneath the buttons) -->
      <template v-if="hasPhotos">
        <q-separator class="q-my-sm" />
        <div class="text-caption text-weight-medium text-grey-7 q-mb-xs">
          Attachments
        </div>
        <div class="photo-grid">
          <div
            v-for="attachment in item.attachments"
            :key="attachment.id"
            class="relative-position photo-cell"
          >
            <q-img
              :src="attachment.file.signed_thumbnail_url || attachment.file.signed_url"
              :ratio="1"
              class="photo-img"
              @click="$emit('viewFile', attachment.file.id)"
            />
            <q-btn
              v-if="!item.is_completed"
              round
              dense
              flat
              size="8px"
              icon="mdi-close"
              color="white"
              class="absolute photo-remove-btn"
              @click.stop="$emit('deletePhoto', attachment)"
            />
          </div>
        </div>
      </template>
    </q-card-section>

    <!-- Assign picker dialog -->
    <q-dialog v-model="showAssignPicker">
      <q-card style="min-width: 300px; max-width: 400px;">
        <q-card-section class="q-pb-none">
          <div class="text-subtitle2">
            Assign to
          </div>
        </q-card-section>
        <q-card-section>
          <user-input-popover
            :model-value="item.assigned_to_user || null"
            outlined
            dense
            clearable
            :multiple="false"
            placeholder="Select staff member..."
            :filter-func="assignFilterFunc"
            class="full-width"
            @update:model-value="onAssignSelected"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<script>
import { doneOptionFor, phraseFor } from 'components/app/checklists/checklistDefinitions'

export default {
  props: {
    item: {
      type: Object,
      required: true
    },
    attendees: {
      type: Array,
      default: () => []
    },
    toggling: {
      type: Boolean,
      default: false
    },
    invoiceStatus: {
      type: Object,
      default: null
    },
    quotationStatus: {
      type: Object,
      default: null
    },
    formStatuses: {
      type: Object,
      default: null
    },
    allowDelete: {
      type: Boolean,
      default: false
    },
    // Task was auto-created because its template is required on the appointment
    fromRequiredForm: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle', 'assign', 'openFileDialog', 'uploaded', 'deletePhoto', 'viewFile', 'invoiceAction', 'quotationAction', 'formAction', 'delete'],
  data () {
    return {
      showAssignPicker: false
    }
  },
  computed: {
    hasPhotos () {
      return this.item.attachments && this.item.attachments.length > 0
    },
    hasLabels () {
      return this.item.is_required || this.item.requires_photo || this.item.requires_invoice || this.item.requires_quotation || this.requiredForms.length > 0
    },
    // Single source of truth for the "outstanding requirement" colour, shared by
    // every requirement type (photo, invoice, quote, form).
    requirementColor () {
      return 'orange-8'
    },
    hasActions () {
      return !this.item.is_completed
    },
    // Invoice / quote / report buttons remain so the document can be viewed even
    // after the task is completed.
    hasDocumentActions () {
      return this.item.requires_invoice || this.item.requires_quotation || this.requiredForms.length > 0
    },
    // The company's active definition of done for each requirement, supplied by
    // the API per status object (with fallbacks for older API responses).
    invoiceDoneWhen () {
      return (this.invoiceStatus && this.invoiceStatus.done_when) || 'FINALISED'
    },
    quotationDoneWhen () {
      return (this.quotationStatus && this.quotationStatus.done_when) || 'FINALISED'
    },
    invoiceSatisfied () {
      if (this.invoiceStatus && this.invoiceStatus.satisfied !== undefined) {
        return !!this.invoiceStatus.satisfied
      }
      return !!(this.invoiceStatus && this.invoiceStatus.has_finalized)
    },
    quotationSatisfied () {
      if (this.quotationStatus && this.quotationStatus.satisfied !== undefined) {
        return !!this.quotationStatus.satisfied
      }
      return !!(this.quotationStatus && this.quotationStatus.has_finalized)
    },
    invoiceLabel () {
      const option = doneOptionFor('invoice', this.invoiceDoneWhen)
      return this.invoiceSatisfied ? option.satisfiedLabel : option.pendingLabel
    },
    quotationLabel () {
      const option = doneOptionFor('quotation', this.quotationDoneWhen)
      return this.quotationSatisfied ? option.satisfiedLabel : option.pendingLabel
    },
    // One descriptor per linked form: its satisfied state, badge label and quick action.
    requiredForms () {
      let ids = this.item.required_form_template_ids || []
      if (typeof ids === 'string') {
        try { ids = JSON.parse(ids) || [] }
        catch (e) { ids = [] }
      }
      if (!Array.isArray(ids)) ids = []
      return ids.map((id) => {
        const status = (this.formStatuses && this.formStatuses[id]) || null
        const satisfied = !!(status && status.satisfied)
        const option = doneOptionFor('report', (status && status.done_when) || 'PUBLISHED')
        const statusLabel = satisfied ? option.satisfiedLabel : option.pendingLabel
        // Name the linked form so the label shows which report is required,
        // not just the generic status
        const linkedForm = (this.item.required_forms || []).find(f => f.id === id)
        const title = (status && status.title) || (linkedForm && linkedForm.title) || null
        return {
          id,
          satisfied,
          label: title ? `${title} · ${statusLabel}` : statusLabel,
          action: this.buildFormAction(id, status)
        }
      })
    },
    formsSatisfied () {
      return this.requiredForms.every(f => f.satisfied)
    },
    isCheckboxDisabled () {
      if (this.item.is_completed) return false
      if (this.item.requires_photo && !this.hasPhotos) return true
      if (this.item.requires_invoice && !this.invoiceSatisfied) return true
      if (this.item.requires_quotation && !this.quotationSatisfied) return true
      if (!this.formsSatisfied) return true
      return false
    },
    disabledReason () {
      const reasons = []
      if (this.item.requires_photo && !this.hasPhotos) reasons.push('upload a photo')
      if (this.item.requires_invoice && !this.invoiceSatisfied) reasons.push(phraseFor('invoice', this.invoiceDoneWhen))
      if (this.item.requires_quotation && !this.quotationSatisfied) reasons.push(phraseFor('quotation', this.quotationDoneWhen))
      if (!this.formsSatisfied) {
        const pendingForm = this.requiredForms.find(f => !f.satisfied)
        const doneWhen = (pendingForm && this.formStatuses && this.formStatuses[pendingForm.id]?.done_when) || 'PUBLISHED'
        reasons.push(`${phraseFor('report', doneWhen)} for the linked report templates`)
      }
      return reasons.length ? `Please ${reasons.join(' and ')} before completing this item` : ''
    },
    invoiceAction () {
      return this.buildAction('invoice', 'invoice', this.invoiceStatus)
    },
    quotationAction () {
      return this.buildAction('quotation', 'quote', this.quotationStatus)
    },
    photoLabel () {
      const count = this.item.attachments ? this.item.attachments.length : 0
      if (count === 0) return 'Photo required'
      return count === 1 ? '1 photo' : `${count} photos`
    },
    attendeeIds () {
      return this.attendees.map(a => a.id)
    },
    assignFilterFunc () {
      return (item) => this.attendeeIds.includes(item.id) || this.can(this.permissions.canEditAppointments)
    }
  },
  methods: {
    onAssignSelected (user) {
      this.$emit('assign', user ? user.id : null)
      this.showAssignPicker = false
    },
    // Orange while a requirement is outstanding, green once it's satisfied. On a
    // completed task everything renders neutrally — the task is done, so a
    // since-tightened definition must not flag it as outstanding.
    dotColor (satisfied) {
      if (this.item.is_completed) {
        return satisfied ? 'green-7' : 'grey-5'
      }
      return satisfied ? 'green-7' : this.requirementColor
    },
    // Build the quick-action descriptor for an invoice/quotation requirement.
    // type drives routing in the parent; noun is the user-facing word.
    buildAction (type, noun, status) {
      const s = status || {}
      if (s.has_finalized) {
        return { type, intent: 'view', id: s.finalized_id, label: `View ${noun}`, color: 'green-7', icon: 'mdi-file-document-outline' }
      }
      if (s.draft_id) {
        return { type, intent: 'continue', id: s.draft_id, label: `Complete ${noun}`, color: 'green', icon: 'mdi-file-document-edit-outline' }
      }
      return { type, intent: 'start', id: null, label: `Start ${noun}`, color: 'green', icon: 'mdi-file-document-plus-outline' }
    },
    // Quick-action descriptor for a single linked form's report.
    buildFormAction (formId, status) {
      const s = status || {}
      if (s.completed_report_id) {
        return { intent: 'view', reportId: s.completed_report_id, formId: s.start_form_template_id || formId, label: 'View report', color: 'green-7', icon: 'mdi-clipboard-check-outline' }
      }
      if (s.draft_report_id) {
        return { intent: 'continue', reportId: s.draft_report_id, formId: s.start_form_template_id || formId, label: 'Complete report', color: 'green', icon: 'mdi-clipboard-edit-outline' }
      }
      return { intent: 'start', reportId: null, formId: s.start_form_template_id || formId, label: 'Start report', color: 'green', icon: 'mdi-clipboard-plus-outline' }
    }
  }
}
</script>

<style lang="scss" scoped>
.checklist-item-card {
  border-radius: 8px;

  .checklist-item-section {
    padding: 8px;
  }

  .checkbox {
    flex-shrink: 0;
  }

  .label-dot {
    gap: 4px;
  }

  // Action buttons stack full-width on mobile, but wrap side-by-side on desktop.
  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media (min-width: 600px) {
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-end;

      // Uniform button widths so multiple tasks line up consistently.
      :deep(.full-width) {
        width: auto !important;
        min-width: 160px;
      }
    }
  }

  .photo-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;

    .photo-cell {
      aspect-ratio: 1;
    }

    .photo-img {
      border-radius: 6px;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }

    .photo-remove-btn {
      top: 4px;
      right: 4px;
      background: rgba(0, 0, 0, 0.5);
      padding: 2px;
    }
  }
}

// Mobile: a bigger touch checkbox and more breathing room.
@media (max-width: 599.98px) {
  .checklist-item-card {
    .checklist-item-section {
      padding: 12px;
    }
  }
}
</style>
